import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Briefcase, ClipboardCheck, Star, Award, Users, Target, Leaf, Quote, ChevronLeft, ChevronRight, Send } from 'lucide-react';
import './Home.css';
import useSEO from '../hooks/useSEO';
import { toast } from 'sonner';

const iconMap = {
  'building': Building2,
  'briefcase': Briefcase,
  'clipboard': ClipboardCheck
};

const Home = () => {
  useSEO({
    title: 'Efruz İnşaat - Lüks İnşaat ve Mimari Çözümler | Ana Sayfa',
    description: 'Efruz İnşaat ile hayalinizdeki projeleri gerçeğe dönüştürün. Lüks konut projeleri, ticari yapılar, proje yönetimi ve anahtar teslim çözümler. 14+ yıllık deneyim, 200+ tamamlanan proje.',
    keywords: 'efruz inşaat, lüks inşaat, konut projeleri, ticari yapılar, proje yönetimi, anahtar teslim, mimari çözümler, inşaat firması, istanbul inşaat'
  });

  const [heroSlides, setHeroSlides] = useState([]);
  const [aboutSlides, setAboutSlides] = useState([]);
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [references, setReferences] = useState([]);
  const [aboutContent, setAboutContent] = useState({});
  const [contactInfo, setContactInfo] = useState({ image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800' });
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [aboutSlideIndex, setAboutSlideIndex] = useState(0);
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [recaptchaToken, setRecaptchaToken] = useState('');

  useEffect(() => {
    // Set up reCAPTCHA callback
    window.handleRecaptchaCallback = (token) => {
      setRecaptchaToken(token);
    };

    const fetchData = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || '/public/api';
        const [heroResponse, aboutResponse, projectsResponse, servicesResponse, referencesResponse, aboutContentResponse, contactInfoResponse] = await Promise.all([
          fetch(`${API_BASE}/hero_slides.php`),
          fetch(`${API_BASE}/about_slides.php`),
          fetch(`${API_BASE}/projects.php`),
          fetch(`${API_BASE}/services.php`),
          fetch(`${API_BASE}/references.php`),
          fetch(`${API_BASE}/about_content.php`),
          fetch(`${API_BASE}/contact_info.php`)
        ]);

        let heroResult, aboutResult, projectsResult, servicesResult, referencesResult, aboutContentResult, contactInfoResult;

        try {
          const heroText = await heroResponse.text();
          console.log('Hero API Response:', heroText.substring(0, 100));
          heroResult = JSON.parse(heroText);
        } catch (e) {
          console.error('Hero API Error:', e);
          heroResult = { success: false, data: [] };
        }

        try {
          const aboutText = await aboutResponse.text();
          console.log('About API Response:', aboutText.substring(0, 100));
          aboutResult = JSON.parse(aboutText);
        } catch (e) {
          console.error('About API Error:', e);
          aboutResult = { success: false, data: [] };
        }

        try {
          const projectsText = await projectsResponse.text();
          console.log('Projects API Response:', projectsText.substring(0, 100));
          projectsResult = JSON.parse(projectsText);
        } catch (e) {
          console.error('Projects API Error:', e);
          projectsResult = { success: false, data: [] };
        }

        try {
          const servicesText = await servicesResponse.text();
          console.log('Services API Response:', servicesText.substring(0, 100));
          servicesResult = JSON.parse(servicesText);
        } catch (e) {
          console.error('Services API Error:', e);
          servicesResult = { success: false, data: [] };
        }

        try {
          const referencesText = await referencesResponse.text();
          console.log('References API Response:', referencesText.substring(0, 100));
          referencesResult = JSON.parse(referencesText);
        } catch (e) {
          console.error('References API Error:', e);
          referencesResult = { success: false, data: [] };
        }

        if (heroResult.success) {
          setHeroSlides(heroResult.data || []);
        }
        if (aboutResult.success) {
          setAboutSlides(aboutResult.data || []);
        }
        if (projectsResult.success) {
          setFeaturedProjects((projectsResult.data || []).slice(0, 4));
        }
        if (servicesResult.success) {
          setServices(servicesResult.data || []);
        }
        if (referencesResult.success) {
          setReferences(referencesResult.data || []);
        }

        // Parse about content
        try {
          const aboutContentText = await aboutContentResponse.text();
          console.log('About Content API Response:', aboutContentText.substring(0, 100));
          aboutContentResult = JSON.parse(aboutContentText);
        } catch (e) {
          console.error('About Content API Error:', e);
          aboutContentResult = { success: false, data: {} };
        }

        if (aboutContentResult.success) {
          setAboutContent(aboutContentResult.data || {});
        }

        // Parse contact info
        try {
          const contactInfoText = await contactInfoResponse.text();
          contactInfoResult = JSON.parse(contactInfoText);
        } catch (e) {
          console.error('Contact Info API Error:', e);
          contactInfoResult = { success: false, data: {} };
        }

        if (contactInfoResult.success && contactInfoResult.data) {
          setContactInfo({
            ...contactInfoResult.data,
            image: contactInfoResult.data.image || ''
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (heroSlides.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [heroSlides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  const goToAboutSlide = (index) => {
    setAboutSlideIndex(index);
  };

  const nextAboutSlide = () => {
    if (aboutSlides.length > 0) {
      setAboutSlideIndex((prev) => (prev + 1) % aboutSlides.length);
    }
  };

  const prevAboutSlide = () => {
    if (aboutSlides.length > 0) {
      setAboutSlideIndex((prev) => (prev - 1 + aboutSlides.length) % aboutSlides.length);
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Check if reCAPTCHA is completed
    if (!recaptchaToken) {
      toast.error('Lütfen reCAPTCHA doğrulamasını tamamlayın!');
      return;
    }
    
    try {
      const API_BASE = process.env.REACT_APP_API_URL || '/public/api';
      const response = await fetch(`${API_BASE}/contacts.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...contactFormData,
          recaptcha_token: recaptchaToken
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('Mesajınız başarıyla gönderildi!', {
          description: 'En kısa sürede size dönüş yapacağız.'
        });
        
        setContactFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        setRecaptchaToken('');
        // Reset reCAPTCHA
        if (window.grecaptcha) {
          window.grecaptcha.reset();
        }
      } else {
        toast.error('Bir hata oluştu!', {
          description: result.error || 'Mesaj gönderilemedi. Lütfen tekrar deneyin.'
        });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Bir hata oluştu!', {
        description: 'Mesaj gönderilemedi. Lütfen tekrar deneyin.'
      });
    }
  };

  return (
    <div className="home-page">
      {/* Hero Section - Slider */}
      <section className="hero-section">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '100px 20px', color: '#fff' }}>
            <p>Yükleniyor...</p>
          </div>
        ) : heroSlides.length > 0 ? (
          <>
            <div className="hero-slider">
              {heroSlides.map((slide, index) => (
                <div
                  key={index}
                  className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
                >
                  <div className="hero-background">
                    <img 
                      src={slide.image}
                      alt={slide.title}
                      className="hero-image"
                    />
                    <div className="hero-overlay"></div>
                  </div>
                  <div className="container">
                    <div className="hero-content">
                      <h1 className="hero-large">{slide.title}</h1>
                      <p className="hero-subtitle body-large">{slide.subtitle}</p>
                      <Link to="/projects" className="btn-primary">
                        Projelerimizi Keşfedin
                        <ArrowRight size={18} />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Slider Controls */}
            <button className="hero-nav-btn hero-nav-prev" onClick={prevSlide} aria-label="Önceki">
              <ChevronLeft size={24} />
            </button>
            <button className="hero-nav-btn hero-nav-next" onClick={nextSlide} aria-label="Sonraki">
              <ChevronRight size={24} />
            </button>

            {/* Slider Dots */}
            <div className="hero-dots">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  className={`hero-dot ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Slide ${index + 1}`}
                />
              ))}
            </div>
          </>
        ) : null}
      </section>

      {/* Page Title Section */}
      <section className="page-title-section">
        <div className="container">
          <h1 className="page-title">{aboutContent.intro?.title || 'Efruz İnşaat'}</h1>
          <p className="page-description">
            {loading ? 'Yükleniyor...' : (aboutContent.intro?.content || 'Hayalinizdeki projeleri gerçeğe dönüştüren, kalite ve mükemmellik odaklı inşaat çözümleri. 14+ yıllık deneyimimizle lüks konut projelerinden ticari yapılara kadar geniş bir hizmet yelpazesi sunuyoruz.')}
          </p>
        </div>
      </section>

      {/* Clients Logo Loop */}
      {references.length > 0 && (
        <section className="clients-logo-loop">
          <div className="clients-logo-container">
            <div className="clients-logo-track">
              {/* First set */}
              {references.map((reference, index) => (
                <div key={index} className="client-logo-item">
                  <img src={reference.logo} alt={reference.name} />
                </div>
              ))}
              {/* Second set for seamless loop */}
              {references.map((reference, index) => (
                <div key={`duplicate-1-${index}`} className="client-logo-item">
                  <img src={reference.logo} alt={reference.name} />
                </div>
              ))}
              {/* Third set for continuous loop */}
              {references.map((reference, index) => (
                <div key={`duplicate-2-${index}`} className="client-logo-item">
                  <img src={reference.logo} alt={reference.name} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services Overview */}
      <section className="services-overview section-padding">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Hizmetlerimiz</h2>
            <p className="body-large">Mükemmeliyeti her projede sunuyoruz</p>
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p>Yükleniyor...</p>
            </div>
          ) : services.length > 0 ? (
            <div className="services-modern-grid">
              {services.slice(0, 3).map((service, index) => {
                const IconComponent = iconMap[service.icon] || Building2;
                return (
                  <div key={service.id} className={`service-modern-card service-card-${index + 1}`}>
                    <div className="service-modern-number">0{index + 1}</div>
                    <div className="service-modern-icon-wrapper">
                      <div className="service-modern-icon-bg"></div>
                      <IconComponent size={28} className="service-modern-icon" />
                    </div>
                    <div className="service-modern-content">
                      <h3 className="service-modern-title">{service.title}</h3>
                      <p className="service-modern-description">{service.description}</p>
                      <div className="service-modern-line"></div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
          <div className="section-cta">
            <Link to="/services" className="btn-secondary">
              <span>Tüm Hizmetlerimizi Görün</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="featured-projects section-padding">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Öne Çıkan Projeler</h2>
            <p className="body-large">Kalite ve mükemmelliyetin örnekleri</p>
          </div>
          <div className="projects-split-grid-home">
            {featuredProjects.map((project, index) => (
              <Link 
                key={project.id} 
                to={`/projects/${project.id}`} 
                className="project-split-card-home"
                data-index={index}
              >
                <div className="project-split-image-wrapper-home">
                  <img src={project.image} alt={project.name} className="project-split-image-home" />
                  <div className="project-split-title-home">
                    <h3 className="project-split-name-home">{project.name}</h3>
                  </div>
                  <div className="project-split-description-home">
                    <p>{project.description}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="section-cta">
            <Link to="/projects" className="btn-primary">
              Tüm Projelerimizi Görün
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview - Slider */}
      <section className="about-preview section-padding">
        <div className="container">
          <div className="about-preview-header">
            <h2 className="heading-1">Neden Efruz İnşaat?</h2>
          </div>
          <div className="about-slider-wrapper">
            <div className="about-slider">
              <div 
                className="about-slider-track"
                style={{ transform: `translateX(-${aboutSlideIndex * 100}%)` }}
              >
                {aboutSlides.map((slide, index) => (
                  <div key={index} className="about-slide">
                    <div className="about-slide-image">
                      <img src={slide.image} alt={slide.title} />
                    </div>
                    <div className="about-slide-content">
                      <h3 className="heading-2">{slide.title}</h3>
                      <p className="body-large">{slide.description}</p>
                      <div className="about-slide-nav-buttons">
                        <button 
                          className="about-slider-btn about-slider-prev"
                          onClick={prevAboutSlide}
                          aria-label="Önceki slide"
                        >
                          <ChevronLeft size={24} />
                        </button>
                        <button 
                          className="about-slider-btn about-slider-next"
                          onClick={nextAboutSlide}
                          aria-label="Sonraki slide"
                        >
                          <ChevronRight size={24} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="about-slider-dots">
              {aboutSlides.map((_, index) => (
                <button
                  key={index}
                  className={`about-slider-dot ${index === aboutSlideIndex ? 'active' : ''}`}
                  onClick={() => goToAboutSlide(index)}
                  aria-label={`Slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
          <div className="about-preview-cta">
            <Link to="/about" className="btn-secondary">
              <span>Daha Fazla Bilgi</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-form-section section-padding">
        <div className="container">
          <div className="contact-form-grid">
            <div className="contact-form-image">
              <img 
                src={contactInfo.image && contactInfo.image.trim() !== '' ? contactInfo.image : 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800'} 
                alt="İletişim" 
                className="contact-form-img"
              />
            </div>
            <div className="contact-form-content">
              <h2 className="heading-1">Bize Ulaşın</h2>
              <p className="body-large form-description">
                Projeleriniz hakkında detaylı bilgi almak için formu doldurun
              </p>
              <form onSubmit={handleContactSubmit} className="contact-form-home">
                <div className="form-row">
                  <div className="form-group">
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      value={contactFormData.name}
                      onChange={handleContactChange}
                      required
                      className="form-input"
                      placeholder="Adınız Soyadınız *"
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      value={contactFormData.email}
                      onChange={handleContactChange}
                      required
                      className="form-input"
                      placeholder="E-posta *"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <input
                      type="tel"
                      id="contact-phone"
                      name="phone"
                      value={contactFormData.phone}
                      onChange={handleContactChange}
                      className="form-input"
                      placeholder="Telefon"
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="text"
                      id="contact-subject"
                      name="subject"
                      value={contactFormData.subject}
                      onChange={handleContactChange}
                      required
                      className="form-input"
                      placeholder="Konu *"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <textarea
                    id="contact-message"
                    name="message"
                    value={contactFormData.message}
                    onChange={handleContactChange}
                    required
                    rows="5"
                    className="form-textarea"
                    placeholder="Mesajınız *"
                  ></textarea>
                </div>

                <div className="form-group">
                  <div 
                    className="g-recaptcha" 
                    data-sitekey="6LeDXBQsAAAAABgD6boBK2-mXJilTug-EojMkvsJ"
                    data-callback="handleRecaptchaCallback"
                  ></div>
                </div>

                <button type="submit" className="btn-primary submit-btn">
                  Mesaj Gönder
                  <Send size={18} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
